import { useState } from 'react'
import { Button, Form, Input, InputNumber, Select, Switch, Tag, Space, App } from 'antd'
import { PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { machinesApi } from '@/features/masters/machines/api'
import { departmentsApi } from '@/features/masters/departments/api'
import { plantsApi } from '@/features/masters/plants/api'
import { usersApi } from '@/features/users/api'
import type { Machine, MachineStatus, CriticalityRating } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const STATUS_OPTIONS: { label: string; value: MachineStatus; color: string }[] = [
  { label: 'Idle', value: 'IDLE', color: 'default' },
  { label: 'Running', value: 'RUNNING', color: 'green' },
  { label: 'Breakdown', value: 'BREAKDOWN', color: 'red' },
  { label: 'Maintenance', value: 'MAINTENANCE', color: 'orange' },
  { label: 'Retired', value: 'RETIRED', color: 'volcano' },
]

const CRITICALITY_OPTIONS: { label: string; value: CriticalityRating; color: string }[] = [
  { label: 'Low', value: 'LOW', color: 'default' },
  { label: 'Medium', value: 'MEDIUM', color: 'blue' },
  { label: 'High', value: 'HIGH', color: 'orange' },
  { label: 'Critical', value: 'CRITICAL', color: 'red' },
]

const SECTION_OPTIONS = [
  'BLOW ROOM', 'CARDING', 'COMBING', 'DRAW FRAME', 'SPEED FRAME',
  'RING FRAME', 'AUTO WINDER', 'TFO', 'UTILITY', 'OTHER',
].map((s) => ({ label: s, value: s }))

export default function MachinesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [deptFilter, setDeptFilter] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Machine | null>(null)

  const canCreate = usePermission('machine.create')
  const canUpdate = usePermission('machine.update')

  const { data, isLoading } = useQuery({
    queryKey: ['machines', page, plantFilter, deptFilter, statusFilter],
    queryFn: () =>
      machinesApi.list({
        page, page_size: 20,
        plant_id: plantFilter,
        department_id: deptFilter,
        machine_status: statusFilter,
      }),
  })

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })
  const { data: deptsData } = useQuery({
    queryKey: ['depts-all', plantFilter],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantFilter }),
  })

  const plantOptions = plantsData?.items.map((p) => ({ label: p.name, value: p.id })) ?? []
  const deptOptions = deptsData?.items.map((d) => ({ label: d.name, value: d.id })) ?? []

  // Sprint 0078 — users for primary/backup technician dropdowns
  const { data: usersData } = useQuery({
    queryKey: ['users-for-machine-tech'],
    queryFn: () => usersApi.list({ page: 1, page_size: 500, is_active: true }),
    staleTime: 5 * 60_000,
  })
  const userOptions = (usersData?.items ?? []).map((u) => ({
    label: `${u.full_name ?? u.username} (${u.username})`,
    value: u.id,
  }))

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ is_active: true, machine_status: 'IDLE', criticality_rating: 'MEDIUM' })
    setDrawerOpen(true)
  }

  const openEdit = (record: Machine) => {
    setEditing(record)
    form.setFieldsValue(record)
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Omit<Machine, 'id' | 'department_name' | 'plant_name'>) =>
      editing ? machinesApi.update(editing.id, values) : machinesApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['machines'] })
      message.success(editing ? 'Machine updated' : 'Machine created')
      setDrawerOpen(false)
    },
    onError: () => message.error('Save failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const statusColor = (s: MachineStatus) =>
    STATUS_OPTIONS.find((o) => o.value === s)?.color ?? 'default'

  const critColor = (c: CriticalityRating) =>
    CRITICALITY_OPTIONS.find((o) => o.value === c)?.color ?? 'default'

  const columns = [
    { title: 'Machine ID', dataIndex: 'machine_id_str', key: 'machine_id_str' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Section', dataIndex: 'section', key: 'section' },
    { title: 'Department', dataIndex: 'department_name', key: 'department_name', render: (v?: string) => v ?? '—' },
    {
      title: 'Status',
      dataIndex: 'machine_status',
      key: 'machine_status',
      render: (v: MachineStatus) => <Tag color={statusColor(v)}>{v}</Tag>,
    },
    {
      title: 'Criticality',
      dataIndex: 'criticality_rating',
      key: 'criticality_rating',
      render: (v: CriticalityRating) => <Tag color={critColor(v)}>{v}</Tag>,
    },
    { title: 'Spindles', dataIndex: 'spindle_count', key: 'spindle_count', render: (v?: number) => v ?? '—' },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Machine) =>
        canUpdate ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
        ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Machines"
        subtitle="Manage machine master records"
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Machine
            </Button>
          )
        }
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="Filter by plant"
          options={plantOptions}
          onChange={(v) => { setPlantFilter(v); setDeptFilter(undefined); setPage(1) }}
          style={{ width: 180 }}
        />
        <Select
          allowClear
          placeholder="Filter by department"
          options={deptOptions}
          onChange={(v) => { setDeptFilter(v); setPage(1) }}
          style={{ width: 180 }}
        />
        <Select
          allowClear
          placeholder="Filter by status"
          options={STATUS_OPTIONS}
          onChange={(v) => { setStatusFilter(v); setPage(1) }}
          style={{ width: 160 }}
        />
      </Space>

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={20}
        onPageChange={(p) => setPage(p)}
      />

      <FormDrawer
        title={editing ? 'Edit Machine' : 'New Machine'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit}
        loading={saveMutation.isPending}
        form={form}
        width={540}
      >
        <Form.Item name="machine_id_str" label="Machine ID" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="name" label="Machine Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="section" label="Section" rules={[{ required: true }]}>
          <Select options={SECTION_OPTIONS} placeholder="Select section" />
        </Form.Item>
        <Form.Item name="department_id" label="Department" rules={[{ required: true }]}>
          <Select options={deptOptions} placeholder="Select department" />
        </Form.Item>
        <Form.Item name="machine_status" label="Status" rules={[{ required: true }]}>
          <Select options={STATUS_OPTIONS} />
        </Form.Item>
        <Form.Item name="criticality_rating" label="Criticality" rules={[{ required: true }]}>
          <Select options={CRITICALITY_OPTIONS} />
        </Form.Item>
        <Form.Item name="spindle_count" label="Spindle Count">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="make" label="Make">
          <Input />
        </Form.Item>
        <Form.Item name="model" label="Model">
          <Input />
        </Form.Item>
        <Form.Item name="pm_interval_hours" label="PM Interval (hours)">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        {/* Sprint 0078 — auto-assign maintenance technician */}
        <Form.Item
          name="primary_technician_id"
          label="Primary Technician (auto-assigned on breakdown)"
          tooltip="When a breakdown is raised on this machine, the WO is automatically assigned to this technician."
        >
          <Select
            allowClear
            showSearch
            placeholder="Select primary technician"
            options={userOptions}
            filterOption={(input, opt) =>
              ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item
          name="backup_technician_id"
          label="Backup Technician"
          tooltip="Used when the supervisor manually reassigns from primary."
        >
          <Select
            allowClear
            showSearch
            placeholder="Select backup technician"
            options={userOptions}
            filterOption={(input, opt) =>
              ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
